import React, { Suspense, lazy as reactLazy, useEffect, useState } from "react";
import type { ComponentType } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/ui/components/ui/toaster";
import { Toaster as Sonner } from "@/ui/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  useParams,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { supabase } from "@/infra/supabase/client";
import { irParaOsAgentes } from "@/config/agentes";
import { logger } from "@/utils/logger";
import LoadingFallback from "@/ui/components/layout/LoadingFallback";
import { HelpFAB } from "@/ui/components/help/HelpFAB";
import { MobileBottomNav } from "@/ui/components/dashboard/MobileBottomNav";
import { ExpirationWarningBanner } from "@/ui/components/ExpirationWarningBanner";

// Rotas críticas (síncronas) - carregam imediatamente
import Index from "./ui/pages/Index";
import Login from "./ui/pages/Login";
import Register from "./ui/pages/Register";
import Recover from "./ui/pages/Recover";
import ResetPassword from "./ui/pages/ResetPassword";
import NotFound from "./ui/pages/NotFound";
import Dashboard from "./ui/pages/Dashboard";
import TempleJourney from "./ui/pages/TempleJourney";
const ContentCalendarPage = lazy(
  () => import("./ui/pages/ContentCalendarPage"),
);
import Notifications from "./ui/pages/Notifications";

/**
 * Wrapper de React.lazy que recarrega a pagina uma vez quando o chunk falha
 * ao ser baixado. Isso acontece tipicamente apos um deploy novo: o index.html
 * antigo em cache aponta para assets com hash que ja foram removidos do servidor,
 * gerando 404 em "Failed to fetch dynamically imported module". O reload busca o
 * index.html novo com os hashes corretos. Um guard em sessionStorage evita loop.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazy<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return reactLazy(async () => {
    try {
      return await factory();
    } catch (error) {
      const RELOAD_KEY = "chunk-reload-ts";
      const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      // So recarrega se nao recarregou nos ultimos 60s (evita loop infinito
      // caso o 404 seja por outro motivo que nao deploy).
      if (Date.now() - lastReload > 60_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        logger.warn(
          "Chunk dinamico falhou ao carregar; recarregando para buscar o build novo",
          { error: String(error) },
        );
        window.location.reload();
        // Mantem o Suspense pendurado enquanto a pagina recarrega.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}

// Rotas admin (lazy) - apenas admins usam
const AdminRoute = lazy(() => import("./ui/pages/admin/AdminRoute"));
const AdminDashboard = lazy(() => import("./ui/pages/admin/AdminDashboard"));
const PreRegistrations = lazy(
  () => import("./ui/pages/admin/PreRegistrations"),
);
const AdminSettings = lazy(() => import("./ui/pages/admin/AdminSettings"));
const OnboardingAdmin = lazy(() => import("./ui/pages/admin/OnboardingAdmin"));
const NotificationsAdmin = lazy(
  () => import("./ui/pages/admin/NotificationsAdmin"),
);
const UserManagementPage = lazy(() =>
  import("@/ui/pages/admin/UserManagementPage").then((m) => ({
    default: m.UserManagementPage,
  })),
);
const AdminTemplesList = lazy(() =>
  import("@/ui/pages/admin/AdminTemplesList").then((m) => ({
    default: m.AdminTemplesList,
  })),
);
const AdminTempleEdit = lazy(() =>
  import("@/ui/pages/admin/AdminTempleEdit").then((m) => ({
    default: m.AdminTempleEdit,
  })),
);
const AdminTempleFrameworkPage = lazy(() =>
  import("@/ui/pages/admin/AdminTempleFrameworkPage").then((m) => ({
    default: m.AdminTempleFrameworkPage,
  })),
);
const AdminTurmasList = lazy(() =>
  import("@/ui/pages/admin/AdminTurmasList").then((m) => ({
    default: m.AdminTurmasList,
  })),
);
const AdminTurmaDetail = lazy(() =>
  import("@/ui/pages/admin/AdminTurmaDetail").then((m) => ({
    default: m.AdminTurmaDetail,
  })),
);
const AdminArchetypesPage = lazy(() =>
  import("@/ui/pages/admin/AdminArchetypesPage").then((m) => ({
    default: m.AdminArchetypesPage,
  })),
);
const AdminPresetsPage = lazy(() =>
  import("@/ui/pages/admin/AdminPresetsPage").then((m) => ({
    default: m.AdminPresetsPage,
  })),
);
const AdminAbilitiesPage = lazy(() =>
  import("@/ui/pages/admin/AdminAbilitiesPage").then((m) => ({
    default: m.default,
  })),
);
const AdminAbilityDetail = lazy(() =>
  import("@/ui/pages/admin/AdminAbilityDetail").then((m) => ({
    default: m.default,
  })),
);
const AdminSettingsPage = lazy(() =>
  import("@/ui/pages/admin/AdminSettingsPage").then((m) => ({
    default: m.AdminSettingsPage,
  })),
);
const AdminExecutionsPage = lazy(() =>
  import("@/ui/pages/admin/AdminExecutionsPage").then((m) => ({
    default: m.AdminExecutionsPage,
  })),
);
const AdminAccountPage = lazy(
  () => import("./ui/pages/admin/AdminAccountPage"),
);
const AdminStudentDetailPage = lazy(
  () => import("./ui/pages/admin/AdminStudentDetailPage"),
);
const AdminResultEditorPage = lazy(
  () => import("./ui/pages/admin/AdminResultEditorPage"),
);
const AdminAIPricingPage = lazy(() =>
  import("./ui/pages/admin/AdminAIPricingPage").then((m) => ({
    default: m.AdminAIPricingPage,
  })),
);
const AdminAIUsagePage = lazy(
  () => import("./ui/pages/admin/AdminAIUsagePage"),
);

const AccessExpiredPage = lazy(() => import("./ui/pages/AccessExpiredPage"));

// Rotas secundárias (lazy) - não são página inicial
const Onboarding = lazy(() => import("./ui/pages/Onboarding"));
const Profile = lazy(() => import("./ui/pages/Profile"));
const AccountData = lazy(() => import("./ui/pages/AccountData"));
const Settings = lazy(() => import("./ui/pages/Settings"));
const MeuNegocio = lazy(() => import("./ui/pages/MeuNegocio"));
const MentorsPage = lazy(() => import("./ui/pages/MentorsPage"));
const Terms = lazy(() => import("./ui/pages/Terms"));
const Privacy = lazy(() => import("./ui/pages/Privacy"));
const LearningCenterPage = lazy(() => import("@/pages/LearningCenterPage"));
const PositioningPage = lazy(() =>
  import("@/ui/pages/PositioningPage").then((m) => ({
    default: m.PositioningPage,
  })),
);
const AbilityHistoryPage = lazy(() =>
  import("@/ui/pages/AbilityHistoryPage").then((m) => ({
    default: m.AbilityHistoryPage,
  })),
);
const HelpCenterPage = lazy(() =>
  import("@/ui/pages/HelpCenterPage").then((m) => ({
    default: m.HelpCenterPage,
  })),
);
const HelpGuidePage = lazy(() =>
  import("@/ui/pages/HelpGuidePage").then((m) => ({
    default: m.HelpGuidePage,
  })),
);

// Wrapper para forçar remount do PositioningPage quando templeId muda
function TemplePageWrapper() {
  const { templeId } = useParams<{ templeId: string }>();
  // key={templeId} força o React a remontar o componente quando templeId muda
  return <PositioningPage key={templeId} />;
}

// Rotas de teste (lazy) - apenas desenvolvimento
const TestPercentageMetricCard = lazy(
  () => import("@/ui/pages/tests/TestPercentageMetricCard"),
);
const TestAchievementBadge = lazy(
  () => import("@/ui/pages/tests/TestAchievementBadge"),
);
const TestMetricItem = lazy(() => import("@/ui/pages/tests/TestMetricItem"));
const TestIndex = lazy(() => import("@/ui/pages/tests/TestIndex"));
const ProfileHeaderCardTestPage = lazy(() =>
  import("@/pages/tests/ProfileHeaderCardTest").then((m) => ({
    default: m.ProfileHeaderCardTestPage,
  })),
);
const EnhancedTempleProgressCardTestPage = lazy(() =>
  import("@/pages/tests/EnhancedTempleProgressCardTest").then((m) => ({
    default: m.EnhancedTempleProgressCardTestPage,
  })),
);
const DifferentiationRadarChartTestPage = lazy(() =>
  import("@/pages/tests/DifferentiationRadarChartTest").then((m) => ({
    default: m.DifferentiationRadarChartTestPage,
  })),
);
const StrategicDefinitionsSectionTestPage = lazy(() =>
  import("@/pages/tests/StrategicDefinitionsSectionTest").then((m) => ({
    default: m.StrategicDefinitionsSectionTestPage,
  })),
);

/** Sai do app e abre a página dos agentes; enquanto isso, o carregando. */
function IrParaOsAgentes() {
  useEffect(() => {
    void irParaOsAgentes();
  }, []);
  return <LoadingFallback />;
}

function ProtectedRoute() {
  const [authState, setAuthState] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");
  const [expired, setExpired] = useState(false);
  const [aluno, setAluno] = useState(false);
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) {
        setAuthState("unauthenticated");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("is_admin, expires_at")
        .eq("id", user.id)
        .single();

      if (
        profile &&
        !profile.is_admin &&
        profile.expires_at &&
        new Date(profile.expires_at) <= new Date()
      ) {
        setExpired(true);
      } else if (!profile?.is_admin) {
        // O app é só do admin (17/10/2026): o aluno usa os agentes no Claude
        // ou no ChatGPT, pela página dos agentes.
        setAluno(true);
      }

      setAuthState("authenticated");
    });
  }, []);

  useEffect(() => {
    if (authState === "unauthenticated") {
      logger.warn("auth", "Acesso não autenticado redirecionado", {
        path: location.pathname,
      });
    }
  }, [authState, location.pathname]);

  if (authState === "loading") return <LoadingFallback />;
  if (authState === "unauthenticated") return <Navigate to="/login" replace />;
  if (expired && location.pathname !== "/access-expired") {
    return <Navigate to="/access-expired" replace />;
  }
  if (aluno) return <IrParaOsAgentes />;
  return (
    <>
      <ExpirationWarningBanner />
      <Outlet />
    </>
  );
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Algo deu errado
        </h1>
        <p className="text-gray-600 mb-6">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Recarregar página
        </button>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 10, // 10 minutos
    },
  },
});

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Rotas publicas */}
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Register />} />
              <Route path="/recuperar" element={<Recover />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/access-expired" element={<AccessExpiredPage />} />

              {/* Rotas protegidas - requer autenticacao */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/jornada" element={<TempleJourney />} />
                <Route path="/calendario" element={<ContentCalendarPage />} />
                <Route path="/notificacoes" element={<Notifications />} />
                <Route path="/perfil" element={<Profile />} />
                <Route path="/dados-conta" element={<AccountData />} />
                <Route path="/boas-vindas" element={<Onboarding />} />
                <Route path="/configuracoes" element={<Settings />} />
                <Route path="/meu-negocio" element={<MeuNegocio />} />
                <Route path="/mentor" element={<MentorsPage />} />
                <Route path="/historico" element={<AbilityHistoryPage />} />
                <Route path="/ajuda" element={<HelpCenterPage />} />
                <Route path="/ajuda/:category" element={<HelpGuidePage />} />
                <Route
                  path="/ajuda/:category/:slug"
                  element={<HelpGuidePage />}
                />
                <Route
                  path="/templo/:templeId/*"
                  element={<TemplePageWrapper />}
                />
              </Route>

              {/* Rotas de teste - apenas em desenvolvimento */}
              {import.meta.env.DEV && (
                <>
                  <Route path="/testes" element={<TestIndex />} />
                  <Route
                    path="/test-percentage-metric-card"
                    element={<TestPercentageMetricCard />}
                  />
                  <Route
                    path="/test-achievement-badge"
                    element={<TestAchievementBadge />}
                  />
                  <Route
                    path="/test-metric-item"
                    element={<TestMetricItem />}
                  />
                  <Route
                    path="/test-profile-header-card"
                    element={<ProfileHeaderCardTestPage />}
                  />
                  <Route
                    path="/test-enhanced-temple-progress"
                    element={<EnhancedTempleProgressCardTestPage />}
                  />
                  <Route
                    path="/test-differentiation-radar"
                    element={<DifferentiationRadarChartTestPage />}
                  />
                  <Route
                    path="/test-strategic-definitions"
                    element={<StrategicDefinitionsSectionTestPage />}
                  />
                </>
              )}

              {/* Rotas admin (lazy) */}
              <Route path="/admin" element={<AdminRoute />}>
                <Route index element={<AdminDashboard />} />
                <Route path="pre-cadastros" element={<PreRegistrations />} />
                <Route path="usuarios" element={<UserManagementPage />} />
                <Route
                  path="usuarios/:id"
                  element={<AdminStudentDetailPage />}
                />
                <Route
                  path="usuarios/:userId/execucoes/:executionId"
                  element={<AdminResultEditorPage />}
                />
                <Route path="templos" element={<AdminTemplesList />} />
                <Route path="templos/novo" element={<AdminTempleEdit />} />
                <Route
                  path="templos/:templeId/editar"
                  element={<AdminTempleEdit />}
                />
                <Route
                  path="templos/:templeId/framework"
                  element={<AdminTempleFrameworkPage />}
                />
                <Route path="turmas" element={<AdminTurmasList />} />
                <Route path="turmas/:turmaId" element={<AdminTurmaDetail />} />
                <Route path="onboarding" element={<OnboardingAdmin />} />
                <Route path="notificacoes" element={<NotificationsAdmin />} />
                <Route path="configuracoes" element={<AdminSettings />} />
                <Route path="api-keys" element={<AdminSettingsPage />} />
                <Route
                  path="configuracoes/precos-ia"
                  element={<AdminAIPricingPage />}
                />
                <Route path="uso-da-ia" element={<AdminAIUsagePage />} />
                <Route path="arquetipos" element={<AdminArchetypesPage />} />
                <Route path="presets" element={<AdminPresetsPage />} />
                <Route path="habilidades" element={<AdminAbilitiesPage />} />
                <Route
                  path="habilidades/:abilityCode"
                  element={<AdminAbilityDetail />}
                />
                <Route path="execucoes" element={<AdminExecutionsPage />} />
                <Route path="minha-conta" element={<AdminAccountPage />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>

          <MobileBottomNav />
          <HelpFAB />
          <Toaster />
          <Sonner />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
